import { Component } from '@theme/component';
import { clamp, mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {object} ScrollShowcaseRefs
 * @property {HTMLElement} stage - The pinned frame that stays in view.
 * @property {HTMLElement[]} [showcasePanels] - The panels that take turns in it.
 * @property {HTMLButtonElement} [advance] - Control that moves on by one panel.
 */

/**
 * A section that holds its frame still while the page scrolls past it, swapping
 * the content of one column as it goes.
 *
 * The pinning is CSS — a tall section with a `position: sticky` stage inside it,
 * so nothing here fights the browser for the scroll position. All this component
 * does is read how far through that tall section the page has travelled and say
 * which panel that distance belongs to. Scrolling therefore stays exactly as
 * fast, as interruptible and as reversible as it is everywhere else on the page.
 *
 * It runs on wide viewports only. Narrow ones get every panel in ordinary flow,
 * one after another, which is both the no-JavaScript state and the state this
 * leaves behind when the viewport shrinks.
 *
 * @extends {Component<ScrollShowcaseRefs>}
 */
class ScrollShowcaseComponent extends Component {
  requiredRefs = ['stage'];

  /** @type {number} */
  #frame = 0;

  /** @type {number} */
  #active = 0;

  /** @type {boolean} */
  #pinned = false;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#syncMode);
    this.#syncMode();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#syncMode);
    this.#unpin();
  }

  /** Moves on to the next panel, for anyone who would rather click than scroll. */
  advance() {
    const count = this.#panels.length;
    if (!this.#pinned || count < 2) return;

    const next = Math.min(this.#active + 1, count - 1);
    const travel = this.offsetHeight - this.refs.stage.offsetHeight;
    const top = this.#documentTop + (travel * next) / (count - 1);

    window.scrollTo({ top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  /** The panel currently in view. */
  get active() {
    return this.#active;
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    const panels = this.refs.showcasePanels;

    if (Array.isArray(panels)) return panels;
    return panels ? [panels] : [];
  }

  /** Distance from the top of the document to the top of this section. */
  get #documentTop() {
    return this.getBoundingClientRect().top + window.scrollY;
  }

  /**
   * Pins on wide viewports and only when there is more than one panel to show:
   * a lone panel has nothing to reveal, so it should not cost the visitor an
   * extra screen of scrolling to pass.
   */
  #syncMode = () => {
    if (mediaQueryLarge.matches && this.#panels.length > 1) this.#pin();
    else this.#unpin();
  };

  #pin() {
    if (this.#pinned) return;

    this.#pinned = true;
    this.dataset.showcaseScroll = '';

    window.addEventListener('scroll', this.#requestUpdate, { passive: true });
    window.addEventListener('resize', this.#requestUpdate);

    this.#update();
  }

  #unpin() {
    if (!this.#pinned) return;

    this.#pinned = false;
    delete this.dataset.showcaseScroll;

    window.removeEventListener('scroll', this.#requestUpdate);
    window.removeEventListener('resize', this.#requestUpdate);
    cancelAnimationFrame(this.#frame);

    // Flow order is the narrow-viewport layout, so leave nothing marked, and
    // drop the progress so anything reading it settles back to its resting
    // position rather than freezing wherever the last scroll left it.
    for (const panel of this.#panels) panel.classList.remove('showcase-panel--active');
    this.style.removeProperty('--showcase-progress');
    if (this.refs.advance) this.refs.advance.hidden = false;
  }

  #requestUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#update);
  };

  /**
   * Reads the scroll position and reflects it onto the panels.
   *
   * Progress runs from the moment the stage settles against the top of the
   * viewport to the moment the section releases it, so the first panel holds
   * while the section is still arriving and the last holds while it leaves.
   */
  #update = () => {
    const panels = this.#panels;
    const count = panels.length;
    if (count < 2) return;

    const travel = this.offsetHeight - this.refs.stage.offsetHeight;
    const progress = travel > 0 ? clamp(-this.getBoundingClientRect().top / travel, 0, 1) : 0;
    const active = clamp(Math.round(progress * (count - 1)), 0, count - 1);

    if (active !== this.#active) this.#active = active;

    // Published so the stylesheet can tie motion to the scroll — see the
    // `showcase-media--scroll-*` rules. Rounded because three decimal places is
    // finer than a pixel of movement, and it keeps the style attribute from
    // churning on scrolls too small to see.
    this.style.setProperty('--showcase-progress', progress.toFixed(3));

    panels.forEach((panel, index) => {
      panel.classList.toggle('showcase-panel--active', index === active);
    });

    if (this.refs.advance) this.refs.advance.hidden = active === count - 1;
  };
}

if (!customElements.get('scroll-showcase')) {
  customElements.define('scroll-showcase', ScrollShowcaseComponent);
}
